import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionItem,
  Box,
  capitalizeFirstLetter,
  CoveragePeriods,
  ElectionAmount,
  EnrollmentStatus,
  Inscription,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import globalTheme from '@/styles/theme';
import theme from '@/styles/theme';
import useMediaQuery from '@/utils/hooks/useMediaQuery';

import CoveragePeriodsTable from './CoveragePeriodsTable';
import ElectionAmountTable from './ElectionAmountTable';
import { EnrollmentCoverageInfoHint } from './EnrollmentCoverageInfoHint';
import { ROLLOVER_HINT_TEXT, RolloverHint } from './RolloverHint';

interface StatusProps {
  iconColor?: string;
}

export const Status = styled(Inscription)<StatusProps>`
  position: relative;
  padding-left: 14px;
  margin-left: ${globalTheme.spacings.xs};
  display: flex;
  color: ${globalTheme.colors.textBody};
  ${globalTheme.fonts.text12Bold};
  flex-direction: row;
  align-items: center;
  &:before {
    content: '';
    position: absolute;
    left: 0px;
    width: 10px;
    height: 10px;
    background: ${({ iconColor }) => iconColor};
    border-radius: 100%;
  }
`;

const AccordionItemWrapper = styled(Box)`
  a {
    color: ${globalTheme.colors.textSecondary};
  }
`;

interface Props {
  coverageDateStart?: string;
  coverageDateEnd?: string;
  submitClaimsDateEnd?: string;
  isInlineMode?: boolean;
  enrollmentStatus?: EnrollmentStatus;
  coveragePeriods?: CoveragePeriods[];
  externalDeductibleMet?: string;
  statusColor?: string;
  width?: string;
  isRollover?: boolean;
  electionPeriods?: ElectionAmount[];
}

export const EnrollmentDates: React.FC<Props> = ({
  coverageDateStart,
  coverageDateEnd,
  submitClaimsDateEnd,
  coveragePeriods,
  externalDeductibleMet,
  enrollmentStatus,
  isInlineMode,
  statusColor,
  width,
  isRollover,
  electionPeriods,
}) => {
  const { isMobile } = useResponsive();
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const singleLimitedPeriods = useMemo(() => {
    if (coveragePeriods && coveragePeriods?.length === 1) {
      return coveragePeriods[0].limitedPeriods;
    }
    return undefined;
  }, [coveragePeriods]);
  const isLessThenLaptopM = useMediaQuery({ mediaQuery: theme.deviceSize.lessThen.laptopM });
  const prefixText = useMemo(() => {
    let currentText = '';
    if (!coveragePeriods?.length) {
      currentText = t("You can't submit claims for this enrollment.");
    } else if (coverageDateStart && coverageDateEnd && submitClaimsDateEnd) {
      currentText = t('Submit expenses with dates of service between');
    } else if (coverageDateStart && coverageDateEnd && !submitClaimsDateEnd) {
      currentText = t('Submit expenses with dates of service between ');
    } else if (coverageDateStart && !coverageDateEnd && submitClaimsDateEnd) {
      currentText = t('Submit expenses with dates of service on or after');
    } else if (coverageDateStart && !coverageDateEnd && !submitClaimsDateEnd) {
      currentText = t('Submit expenses with dates of service on or after');
    } else if (!coverageDateStart && !coverageDateEnd && !submitClaimsDateEnd) {
      currentText = t('Submit expenses with any date of service');
    } else if (!coverageDateStart && !coverageDateEnd && submitClaimsDateEnd) {
      currentText = t('Submit expenses with any date of service until ');
    }
    return <Text size="small" weight={500} color="textSecondary">{`${currentText} `}</Text>;
  }, [coverageDateEnd, coverageDateStart, submitClaimsDateEnd, coveragePeriods, t]);
  const rangeText = useMemo(() => {
    let currentText = '';
    if (!coveragePeriods?.length) {
      currentText = '';
    } else if (coverageDateStart && coverageDateEnd && submitClaimsDateEnd) {
      currentText = `${coverageDateStart} – ${coverageDateEnd}`;
    } else if (coverageDateStart && coverageDateEnd && !submitClaimsDateEnd) {
      currentText = `${coverageDateStart} – ${coverageDateEnd}`;
    } else if (coverageDateStart && !coverageDateEnd && submitClaimsDateEnd) {
      currentText = `${coverageDateStart}`;
    } else if (coverageDateStart && !coverageDateEnd && !submitClaimsDateEnd) {
      currentText = `${coverageDateStart}`;
    } else if (!coverageDateStart && !coverageDateEnd && !submitClaimsDateEnd) {
      currentText = '';
    } else if (!coverageDateStart && !coverageDateEnd && submitClaimsDateEnd) {
      currentText = `${submitClaimsDateEnd}`;
    }
    return (
      <Text
        data-testid="EnrollmentDates_coverage-period"
        size="small"
        margin={{ top: isInlineMode ? '' : 'xxs' }}
        weight={700}
        color="textBody"
        style={{ display: 'inline-block' }}
      >
        {`${currentText}`}
      </Text>
    );
  }, [coverageDateEnd, coverageDateStart, isInlineMode, submitClaimsDateEnd, coveragePeriods]);

  const postfixText = useMemo(() => {
    let currentText = '';
    if (!coveragePeriods?.length) {
      currentText = '';
    } else if (coverageDateStart && coverageDateEnd && submitClaimsDateEnd) {
      currentText = `${submitClaimsDateEnd}`;
    } else if (coverageDateEnd && !coverageDateEnd && submitClaimsDateEnd) {
      currentText = `${submitClaimsDateEnd}`;
    }
    if (!currentText) {
      return null;
    }
    return (
      <>
        <Text data-testid="EnrollmentDates_last-date-to-submit" size="small" color="textSecondary">
          {t(' until ')}
        </Text>
        <Text
          style={{ display: 'inline-block' }}
          color="textBody"
          size="small"
          weight={700}
          margin={{ top: isInlineMode ? '' : 'xxs' }}
        >
          {currentText}
        </Text>
      </>
    );
  }, [coveragePeriods?.length, coverageDateStart, coverageDateEnd, submitClaimsDateEnd, isInlineMode, t]);

  const enrollmentDates = useMemo(() => {
    const isMoreThenOneCoveragePeriod = coveragePeriods && coveragePeriods?.length > 1;
    const isMoreThenOneElectionPeriod = electionPeriods && electionPeriods?.length > 1;
    return (
      <Box>
        <Box
          margin={{
            left: 'xxs',
            top: isMoreThenOneCoveragePeriod || isMoreThenOneElectionPeriod ? '0' : 'spacing12',
          }}
          direction="row"
          wrap
          align="center"
        >
          <Box direction="row" align="center" data-testid="EnrollmentPeriodCard_enrollment-status">
            <Inscription color="textSecondary" size="small" style={{ fontWeight: 500 }}>
              {t('Enrollment status:')}
            </Inscription>
            <Status
              lineHeight="24px"
              iconColor={statusColor}
              data-testid="EnrollmentPeriodCard_enrollment-status-value"
            >
              {enrollmentStatus && capitalizeFirstLetter(enrollmentStatus)}
            </Status>
          </Box>
          <Box
            align="center"
            justify="center"
            height="4px"
            width="4px"
            round="full"
            background="border2"
            margin={{ horizontal: 'xs' }}
          />
          <Box direction="row" wrap>
            <Text size="medium" style={{ lineHeight: 1 }} margin={{ right: 'spacing4' }}>
              {prefixText}
            </Text>
            <Text size="medium" style={{ lineHeight: 1 }}>
              {rangeText}
              {postfixText}
            </Text>
          </Box>
          {(singleLimitedPeriods && singleLimitedPeriods.length) ||
          (coveragePeriods && coveragePeriods?.length === 1 && externalDeductibleMet) ? (
            <Box
              width={{ max: '16px', min: '16px' }}
              margin={{ left: 'spacing8' }}
              pad={{ bottom: 'spacing2' }}
            >
              <EnrollmentCoverageInfoHint
                limitedPeriods={singleLimitedPeriods}
                externalDeductibleMet={externalDeductibleMet}
                coveragePeriods={coveragePeriods}
              />
            </Box>
          ) : null}
        </Box>
        {isRollover && (
          <Inscription
            color="textSecondary"
            size="small"
            style={{ fontWeight: 500, marginLeft: theme.spacings.xxs, marginTop: theme.spacings.xxxs }}
          >
            {t(ROLLOVER_HINT_TEXT)}
          </Inscription>
        )}
      </Box>
    );
  }, [
    statusColor,
    enrollmentStatus,
    prefixText,
    rangeText,
    postfixText,
    coveragePeriods,
    t,
    isRollover,
    singleLimitedPeriods,
    externalDeductibleMet,
    electionPeriods,
  ]);

  if (isInlineMode) {
    if (
      (coveragePeriods && coveragePeriods?.length > 1) ||
      (electionPeriods && electionPeriods?.length > 1)
    ) {
      return (
        <Box
          style={{
            position: 'relative',
            cursor: 'pointer',
          }}
          margin={{ top: isInlineMode ? 'm' : '0' }}
        >
          <AccordionItemWrapper width="100%">
            <AccordionItem
              isExpanded={expanded}
              onChange={setExpanded}
              text={enrollmentDates}
              isLeftArrow
              iconSize="20px"
            >
              <>
                <Box height="1px" background="border1" margin={{ vertical: 'm' }} />
                <Box direction={isLessThenLaptopM ? 'column' : 'row'} gap="spacing12">
                  {!!coveragePeriods?.length && (
                    <Box
                      background="canvas"
                      pad={{ bottom: 'xs', horizontal: 'm', top: 'm' }}
                      round="small"
                      width="100%"
                      height="fit-content"
                    >
                      <Text size="small" weight="bold" color="textTitle" margin={{ bottom: 'xs' }}>
                        {t('All coverage periods')}
                      </Text>
                      <CoveragePeriodsTable
                        coveragePeriods={coveragePeriods}
                        externalDeductibleMet={externalDeductibleMet}
                      />
                    </Box>
                  )}
                  {!!electionPeriods?.length && (
                    <Box
                      background="canvas"
                      pad={{ bottom: 'xs', horizontal: 'm', top: 'm' }}
                      round="small"
                      width="100%"
                      height="fit-content"
                    >
                      <Text size="small" weight="bold" color="textTitle" margin={{ bottom: 'xs' }}>
                        {t('All election amounts')}
                      </Text>
                      <ElectionAmountTable electionAmounts={electionPeriods} />
                    </Box>
                  )}
                </Box>
              </>
            </AccordionItem>
          </AccordionItemWrapper>
        </Box>
      );
    }
    return enrollmentDates;
  }
  return (
    <Box
      width={isMobile ? '100%' : width || '100%'}
      direction="row"
      justify="between"
      background="border1"
      margin={{ top: '-12px' }}
      style={{ zIndex: -1 }}
      round={{ corner: 'bottom', size: 'container1Round' }}
      border={{ color: 'border2', size: 'small' }}
      pad={{ vertical: 'spacing8', horizontal: 'spacing24', top: 'spacing20' }}
      data-testid="EnrollmentDates_container"
    >
      <Text size="medium" style={{ lineHeight: 1 }}>
        {prefixText}
        {rangeText}
        {postfixText}
      </Text>
      {isRollover && <RolloverHint />}
    </Box>
  );
};
